import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { isString } from '../../../models/utils';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { DrObject } from '../../../models/dr-object';
import { Endpoint, HelpIndex, UpdateStatusType } from '../../../models/enums';
import { Group, GroupViewModel } from '../../../models/group';
import { Role } from '../../../models/role';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { NameDescriptionComponent } from '../../name-description/name-description.component';

class CreateEditEntityDataType {
    entity: GroupViewModel;
    isEdit: boolean;
    entityType: string;
    validateName = true;
    validateNameRequest: {
        endpoint: Endpoint,
        request: (name: string) => void
    };
    endpoint: Endpoint;
    title: string;
    name: string;
    description: string;
    isBusyMessage: string;
    helpIndex?: HelpIndex;
    updateStatusType?: UpdateStatusType;
    autoSelectEntity?: boolean;
    responseObjectName?: string;
    responseObjectHandleName = 'handle';
    selectedRole: string;
    request: any;
    selectedRoleHandle: string;
    isSystemUser: boolean;

    constructor(value?: Partial<CreateEditEntityDataType>) {
        if (value) {
            Object.assign(this, value);

            this.isEdit = isString(this.name) && (this.name !== '');
            const capitalizedTitle = this.entity.objectType.replace(/([a-z](?=[A-Z]))/g, '$1 ').replace(/\b\w/g, l => l.toUpperCase());
            if (this.isEdit) {
                if (!this.title) {
                    this.title = `Edit ${capitalizedTitle}`;
                }
                if (!this.isBusyMessage) {
                    this.isBusyMessage = `Updating ${capitalizedTitle} ...`;
                }
            } else {
                if (!this.title) {
                    this.title = this.entityType !== 'template' ? `Create ${capitalizedTitle}` : `New ${capitalizedTitle}`;
                }
                if (!this.isBusyMessage) {
                    this.isBusyMessage = `Creating ${capitalizedTitle} ...`;
                }
            }
        }
    }
}

@Component({
    standalone: false,
    selector: 'res-create-edit-entity-group-modal',
    templateUrl: './create-edit-entity-group-modal.component.html',
    styleUrls: ['./create-edit-entity-group-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})


export class CreateEditEntityGroupModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(NameDescriptionComponent, { static: true }) nameDescriptionComponent: NameDescriptionComponent;

    modalAutomationId = 'group-template';
    verifyRequiredValidator = true;
    isBusy = false;
    isBusyMessage = '';
    modalClass = 'group-modal';
    roleList: Array<Role>;
    entityData: CreateEditEntityDataType = new CreateEditEntityDataType();
    roleHandles: Array<string>;
    isNameDisabled = false;
    isSystemUser: boolean;

    constructor(
        private modalService: BsModalService,
        private commonService: CommonService,
        private modalRef: BsModalRef,
        private restService: RestService
    ) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        this.loadEntityData();

        if (this.entityData.isSystemUser) {
            this.loadSystemRoles();
        } else {
            this.restService.post(Endpoint.orgManager_listRoles, {
                objectType: 'ORGANIZATION',
                systemScope: this.isSystemUser
            }).then(response => {
                this.roleList = response.roles;

                if (this.entityData.entity.model.roleHandles && this.entityData.entity.model.roleHandles[0] !== Role.noRoleHandle) {
                    const selectedRole = (this.roleList.find(role => {
                        return role.handle === this.entityData.entity.model.roleHandles[0];
                    }).name);
                    this.entityData.selectedRole = selectedRole;
                }
            });
        }
    }

    loadSystemRoles(): void {
        this.isBusy = true;
        this.isBusyMessage = 'Loading ...'
        this.restService.post(Endpoint.realmManager_listSystemRoles, { objectType: 'ALL', systemScope: this.isSystemUser }).then(responseListRoles => {
            if (responseListRoles && responseListRoles.roles) {
                this.roleList = responseListRoles.roles.map(role => new Role(role));

                if (this.entityData.entity.model.roleHandles && this.entityData.entity.model.roleHandles[0] !== Role.noRoleHandle) {
                    const selectedRole = (this.roleList.find(role => {
                        return role.handle === this.entityData.entity.model.roleHandles[0];
                    })?.name);
                    this.entityData.selectedRole = selectedRole;
                }
            }

            this.isBusy = false;
        }, error => {
            this.isBusy = false;
        });
    }

    loadEntityData(): void {
        const group = (this.modalData && this.modalData.group instanceof Group) ? this.modalData.group as Group : new Group();

        if (group.name) {
            this.isNameDisabled = true;
        }
        this.isSystemUser = this.modalData?.isSystemUser;

        this.entityData = new CreateEditEntityDataType({
            entity: new GroupViewModel(group),
            title: isString(group.name) ? (this.isSystemUser ? 'Edit System Group' : 'Edit Organization Group') : (this.isSystemUser ? 'New System Group' : 'New Organization Group'),
            selectedRole: Role.noRoleHandle,
            name: isString(group.name) ? group.name : '',
            description: isString(group.description) ? group.description : '',
            validateNameRequest: {
                endpoint: Endpoint.viewManager_validateName,
                request: (name) => {
                    return {
                        name: this.nameDescriptionComponent.name,
                        projectHandle: null,
                        objectType: this.entityData.entity.objectType,
                        systemScope: this.isSystemUser
                    };
                }
            },
            helpIndex: !this.isSystemUser ? HelpIndex.add_edit_org_group_dlg : HelpIndex.add_edit_system_group_dlg,
            isSystemUser: this.modalData?.isSystemUser
        });

        this.nameDescriptionComponent.drObject = this.entityData.entity;
    }

    ok(): void {
        if (this.entityData) {
            if (this.entityData.selectedRole !== Role.noRoleHandle) {
                this.entityData.selectedRoleHandle = (this.roleList.find(role => {
                    return role.name === this.entityData.selectedRole;
                }).handle);
            }

            if (this.entityData.selectedRoleHandle) {
                if (this.entityData.isEdit) {
                    this.restService.post(Endpoint.orgManager_updateGroup, {
                        group: {
                            name: this.nameDescriptionComponent.name,
                            description: this.nameDescriptionComponent.description,
                            handle: this.entityData.entity.model.handle,
                            roleHandles: [this.entityData.selectedRoleHandle],
                            roles: [{ name: this.entityData.selectedRole, handle: this.entityData.selectedRoleHandle }]
                        },
                        systemScope: this.isSystemUser
                    }).then(response => {
                        this.isBusy = false;
                        this.closeModal(true, response.group);
                    }, error => {
                        this.isBusy = false;
                    });
                } else {
                    const endpoint = this.isSystemUser ? Endpoint.adminOrgManager_createGroup : Endpoint.orgManager_createGroup;
                    const request: any = {
                        name: this.nameDescriptionComponent.name,
                        description: this.nameDescriptionComponent.description,
                        roleHandles: [this.entityData.selectedRoleHandle],
                        systemScope: this.isSystemUser
                    }
                    if (this.isSystemUser) {
                        request.organizationName = 'super_system_customer';
                    }
                    this.restService.post(endpoint, request).then(response => {
                        this.isBusy = false;
                        this.closeModal(true, response.group);
                    }, error => {
                        this.isBusy = false;
                    });
                }
            }
        }
    }

    okEnabled(): boolean {
        let enabled = false;
        if (!this.isBusy) {
            if (!DrObject.isEmpty(this.nameDescriptionComponent.name) && this.entityData.selectedRole !== 'NOROLEHANDLE') {
                if (this.nameDescriptionComponent.isNameDirty() && (this.nameDescriptionComponent.formValid)) {
                    enabled = true;
                } else if ((this.nameDescriptionComponent.isDescriptionDirty()) && (this.nameDescriptionComponent.formValid)) {
                    enabled = true;
                } else if ((!this.nameDescriptionComponent.isDescriptionDirty() && !this.nameDescriptionComponent.isNameDirty()) &&
                    (this.modalData.group && this.modalData.group.groupRole && this.modalData.group.groupRole !== this.entityData.selectedRole)) {
                    enabled = true;
                }
            }
        }
        return enabled;
    }

    hideRoleOption(role) {
        if (this.isSystemUser) {
            const sysAdminRole: any = this.roleList.find(role => role.name === 'System Administrator');
            const sysITAdminRole: any = this.roleList.find(role => role.name === 'IT Administrator');
            let userHasSysRole: any = this.commonService.currentUser.roles.find(role => role.name === 'System Administrator');
            let userHasITRole: any = this.commonService.currentUser.roles.find(role => role.name === 'IT Administrator');

            if (this.commonService.currentUser.systemRoles && !userHasSysRole) {
                userHasSysRole = this.commonService.currentUser.systemRoles.find(role => role.name === 'System Administrator');
            }

            if (!userHasITRole && this.entityData.isEdit && this.entityData.entity.model.roleHandles[0] === sysITAdminRole.handle) {
                return role.handle === sysITAdminRole.handle ? false : true;
            }

            if (userHasITRole && !this.entityData.isEdit && (role.handle === sysITAdminRole.handle || role.handle === sysAdminRole.handle)) {
                return false;
            }

            if (userHasSysRole && role.handle === sysITAdminRole.handle) {
                return true;
            }

            if (userHasITRole) {
                return false;
            }

            if (!userHasSysRole) {
                if (role.handle === sysITAdminRole.handle) {
                    return true;
                }

                if (this.entityData.isEdit) {
                    return this.entityData.entity.model.roleHandles[0] === sysAdminRole.handle ? (role.handle === sysAdminRole.handle ? false : true) : (role.handle === sysAdminRole.handle);
                }

                if (!this.entityData.isEdit) {
                    return role.handle === sysAdminRole.handle;
                }
            }
        }
    }
}
